import { useFormContext } from 'react-hook-form'
import { useFieldArray } from 'react-hook-form'

import { LogicHandler } from '@fortem-monorepo/react-spa'
import { DateRangePicker } from '@fortem-monorepo/react-ui'
import { SearchSelect } from '@fortem-monorepo/react-ui'
import { Button } from '@fortem-monorepo/react-ui'
import { FormField, FormItem, FormLabel } from '@fortem-monorepo/react-ui'
import { Input } from '@fortem-monorepo/react-ui'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@fortem-monorepo/react-ui'

import { CustomJobFormType } from '@/pages/monitoring/custom-jobs/components/CustomJobFormSchema'

export interface CustomJobFormFieldsProps {
  showCustomArgs?: boolean
  toggleShowCustomArgs?: () => void
  taskOptions: Array<{ label: string; value: string }>
}

export const CustomJobFormFields: React.FC<CustomJobFormFieldsProps> = ({
  showCustomArgs,
  toggleShowCustomArgs,
  taskOptions,
}) => {
  const form = useFormContext<CustomJobFormType>()
  const { control } = form

  const {
    fields: recipientFields,
    append: addRecipientLine,
    remove: removeRecipientLine,
  } = useFieldArray({
    control,
    name: 'recipients',
  })

  const {
    fields: senderFields,
    append: addSenderLine,
    remove: removeSenderLine,
  } = useFieldArray({
    control,
    name: 'sender',
  })

  const {
    fields: subjectFields,
    append: addSubjectLine,
    remove: removeSubjectLine,
  } = useFieldArray({
    control,
    name: 'subject',
  })

  return (
    <>
      <div className="space-y-3">
        <FormField
          name="taskname"
          control={control}
          render={({ field }) => (
            <FormItem className={'flex items-center gap-2'}>
              <FormLabel className={'w-[80px]'}>Script</FormLabel>
              <SearchSelect
                className={'flex-1'}
                placeholder="Select a task..."
                size="sm"
                value={field.value}
                setValue={field.onChange}
                options={taskOptions}
              />
            </FormItem>
          )}
        />

        {/* <Separator /> */}

        <FormField
          name="ed_dt"
          control={control}
          render={({ field: { value: ed_dt, onChange: setEdDt } }) => {
            return (
              <FormField
                name="st_dt"
                control={control}
                render={({ field: { value: st_dt, onChange: setStDt } }) => (
                  <FormItem className={'flex flex-row items-center gap-2'}>
                    <FormLabel className={'whitespace-nowrap w-[80px]'}>Dates</FormLabel>
                    <DateRangePicker
                      className={'flex-1'}
                      size="sm"
                      date={{ from: st_dt, to: ed_dt }}
                      setDate={(date) => {
                        // @ts-ignore
                        setStDt(date.from)
                        setEdDt(date.to)
                      }}
                    />
                  </FormItem>
                )}
              />
            )
          }}
        />

        {/* <Separator /> */}

        <FormField
          name="recipients"
          control={control}
          render={() => {
            return (
              <FormItem>
                {recipientFields.map((field, index) => {
                  return (
                    <LogicHandler
                      key={field.id}
                      control={control}
                      name={`recipients.${index}`}
                      title={index === 0 ? 'Recipients' : undefined}
                      handleAddRow={() => {
                        addRecipientLine({
                          logic: 'or',
                          matcher: 'contains',
                          value: '',
                        })
                        form.trigger()
                      }}
                      handleRemoveRow={() => removeRecipientLine(index)}
                    />
                  )
                })}
              </FormItem>
            )
          }}
        />

        {/* <Separator /> */}

        <FormField
          name="sender"
          control={control}
          render={() => {
            return (
              <FormItem>
                {senderFields.map((field, index) => {
                  return (
                    <LogicHandler
                      key={field.id}
                      control={control}
                      name={`sender.${index}`}
                      title={index === 0 ? 'Sender' : undefined}
                      handleAddRow={() => {
                        addSenderLine({
                          logic: 'or',
                          matcher: 'contains',
                          value: '',
                        })
                        form.trigger()
                      }}
                      handleRemoveRow={() => removeSenderLine(index)}
                    />
                  )
                })}
              </FormItem>
            )
          }}
        />

        {/* <Separator /> */}

        <FormField
          name="subject"
          control={control}
          render={() => {
            return (
              <FormItem>
                {subjectFields.map((field, index) => {
                  return (
                    <LogicHandler
                      key={field.id}
                      control={control}
                      name={`subject.${index}`}
                      title={index === 0 ? 'Subject' : undefined}
                      handleAddRow={() => {
                        addSubjectLine({
                          logic: 'or',
                          matcher: 'contains',
                          value: '',
                        })
                        form.trigger()
                      }}
                      handleRemoveRow={() => removeSubjectLine(index)}
                    />
                  )
                })}
              </FormItem>
            )
          }}
        />

        {/* <Separator /> */}

        <FormItem>
          <Button
            size="sm"
            variant="outline"
            type="button"
            onClick={() => {
              toggleShowCustomArgs && toggleShowCustomArgs()
            }}
          >
            {showCustomArgs ? 'Hide' : 'Show'} custom arguments
          </Button>
          {showCustomArgs && (
            <Table className={'mt-3 rounded-md'}>
              <TableHeader>
                <TableRow>
                  <TableHead>Key</TableHead>
                  <TableHead>Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <Input inputSize="sm" />
                  </TableCell>
                  <TableCell>
                    <Input inputSize="sm" />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </FormItem>
      </div>
    </>
  )
}
